/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { notifyError } from "common/utils/app-monitoring/notify";
import { createJotaiPiniaFacadeForReact } from "common/vue/createJotaiPiniaFacadeForReact";
import {
  jotaiStore,
  SPANGLE_MODES,
  lastSpangledDimensionsAtom,
  spangleVariationsAtom,
  spangleModeAtom,
  selectedAlgorithmAtom,
  originalArtworkUrlAtom,
  spanglesMaxImageCountAtom,
  allAPIsCompletedStateAtom,
  invalidSpangleSelectedAtom,
  isSpangleReorderingAtom,
  completedSpangleDataAtom
} from "./spangles-module.atoms";
import type { SpangleVariation, SpangleData } from "./spangles-module.atoms";
// eslint-disable-next-line import/no-cycle
import { dtfStore } from "./store";

export { SPANGLE_MODES };

const defaultVariation: Partial<SpangleVariation> = { isLoading: true };

export const useSpanglesStore = createJotaiPiniaFacadeForReact({
  jotaiStore,

  state: {
    lastSpangledDimensions: lastSpangledDimensionsAtom,
    spangleVariations: spangleVariationsAtom,
    spangleMode: spangleModeAtom,
    selectedAlgorithm: selectedAlgorithmAtom,
    originalArtworkUrl: originalArtworkUrlAtom,
    spanglesMaxImageCount: spanglesMaxImageCountAtom,
    allAPIsCompletedState: allAPIsCompletedStateAtom,
    invalidSpangleSelected: invalidSpangleSelectedAtom,
    isSpangleReordering: isSpangleReorderingAtom,
    completedSpangleData: completedSpangleDataAtom
  },

  getters: {
    spangleIt() {
      return this.spangleMode === SPANGLE_MODES.spangleIt;
    },
    spangleSizeEditing() {
      return this.spangleMode === SPANGLE_MODES.editSize;
    },
    spangleSizeEdited() {
      return this.spangleMode === SPANGLE_MODES.sizeEdited;
    },
    spanglesLoading() {
      return this.spangleMode === SPANGLE_MODES.spanglesLoading;
    },
    isInitialSpangle() {
      return this.spangleVariations.length === 0;
    },
    allAPIsCompleted() {
      return this.spangleVariations.every((variation: SpangleVariation) => !variation.isLoading);
    },
    allAPIsFailed() {
      return this.spangleVariations.every(
        (variation: SpangleVariation) => !variation.isLoading && variation.error
      );
    },
    isSelectedVersionLoading() {
      const selectedVersion = this.spangleVariations.find(
        (variation: SpangleVariation) => variation.algorithm === this.selectedAlgorithm
      );
      return selectedVersion?.isLoading;
    },
    selectedVersion() {
      return this.spangleVariations.find(
        (variation: SpangleVariation) => variation.algorithm === this.selectedAlgorithm
      );
    },
    isFirstSpangle() {
      return this.spangleVariations.length === 0;
    }
  },

  actions: {
    setSpangleMode(mode: string) {
      this.spangleMode = mode;
    },
    setSpangleVariations(variations: SpangleVariation[]) {
      this.spangleVariations = variations;
    },
    setLastSpangledDimensions(dimensions: { width: number | null; height: number | null }) {
      this.lastSpangledDimensions = { ...dimensions };
    },
    setSelectedAlgorithm(algorithm: string | null) {
      this.selectedAlgorithm = algorithm;
    },
    setOriginalArtworkUrl(url: string) {
      this.originalArtworkUrl = url;
    },
    setInvalidSpangleSelected(invalid: boolean) {
      this.invalidSpangleSelected = invalid;
    },
    setIsSpangleReordering(isSpangleReordering: boolean) {
      this.isSpangleReordering = isSpangleReordering;
    },
    resetSpangles() {
      this.$reset();
    },
    addCompletedSpangleData(data: SpangleData) {
      this.completedSpangleData = [...this.completedSpangleData, data];
    },
    resetCompletedSpangleData() {
      this.completedSpangleData = [];
    },
    selectVersion({ version }: { version: SpangleVariation | undefined }) {
      if (!version) return;
      const store = dtfStore;
      this.setSelectedAlgorithm(version.algorithm);

      if (version.isLoading) return;

      this.setInvalidSpangleSelected(version.error);
      const imageUrl = version.error ? this.originalArtworkUrl : version.imageUrl;

      store.updateArtworkValues({
        index: 0,
        spangleCsv: version.spangleCsv,
        spanglePagesInfo: version.spanglePagesInfo,
        spanglePositionsJson: version.positionsJson,
        spangledImageUrl: imageUrl
      });
    },
    prepareVariations({ isRetry }: { isRetry: boolean }) {
      const {
        spangleVariations,
        selectedAlgorithm,
        allAPIsFailed,
        isFirstSpangle,
        isSpangleReordering
      } = this;
      const { spangleAlgorithms } = dtfStore;

      let variationsObject: SpangleVariation[] = [];

      if (isRetry) {
        variationsObject = spangleVariations.map((variation: SpangleVariation) => {
          if (variation.algorithm === selectedAlgorithm || allAPIsFailed) {
            return {
              ...defaultVariation,
              algorithm: variation.algorithm,
              spanglePagesInfo: variation.spanglePagesInfo
            } as SpangleVariation;
          }
          return variation;
        });
      } else {
        const isFreshCall = isFirstSpangle || isSpangleReordering;
        const algorithmList = isFreshCall ? spangleAlgorithms : spangleVariations;
        variationsObject = algorithmList.map((item: unknown) => ({
          ...(isFreshCall ? defaultVariation : (item as SpangleVariation)),
          algorithm: isFreshCall ? (item as string) : (item as SpangleVariation).algorithm,
          spanglePagesInfo: isFreshCall ? null : (item as SpangleVariation).spanglePagesInfo,
          isLoading: true
        })) as SpangleVariation[];
      }

      if (variationsObject.length > 0 && (!selectedAlgorithm || isSpangleReordering)) {
        this.setSelectedAlgorithm(variationsObject[0]!.algorithm);
      }

      this.setSpangleVariations(variationsObject);
    },
    updateSpangleVersion({ spangle }: { spangle: SpangleData }) {
      const { spangleVariations } = this;

      const updatedList = spangleVariations.map((variation: SpangleVariation) =>
        variation.algorithm === spangle.algorithm
          ? { ...variation, ...spangle, isLoading: false }
          : variation
      );

      this.setSpangleVariations(updatedList);
    },
    // eslint-disable-next-line max-lines-per-function
    changeColor({ colorValues }: { colorValues: Record<string, unknown> }) {
      const variationToUpdate = this.spangleVariations.filter(
        ({ isLoading, error, spanglePagesInfo }: SpangleVariation) => {
          const variationColorCount = spanglePagesInfo
            ? Object.values(spanglePagesInfo).filter(color => typeof color === "object").length
            : 0;

          const colorValuesCount = Object.keys(colorValues).length;

          return !isLoading && !error && colorValuesCount === variationColorCount;
        }
      );

      if (variationToUpdate.length === 0) return;

      /* eslint-disable camelcase */
      // eslint-disable-next-line max-lines-per-function
      variationToUpdate.map((variation: SpangleVariation) => {
        const { total_beads_count, time_taken } = variation.spanglePagesInfo as Record<
          string,
          unknown
        >;

        return api.dtf
          .updateSpangleColor({
            positions_json: variation.positionsJson,
            pages_info: {
              ...colorValues,
              total_beads_count,
              time_taken
            }
          })
          .then((result: Record<string, unknown>) => {
            const store = dtfStore;
            const imageUrl = result["design_url"] || result["design_base64"];
            const hasValidImage = Boolean(imageUrl);
            const effectiveImageUrl = hasValidImage ? imageUrl : variation.imageUrl;

            this.updateSpangleVersion({
              spangle: {
                ...variation,
                imageUrl: effectiveImageUrl as string,
                ...(result["pages_info"] ? { spanglePagesInfo: result["pages_info"] } : {}),
                ...(result["positions_json"] ? { positionsJson: result["positions_json"] } : {}),
                error: !effectiveImageUrl
              }
            });

            if (variation.algorithm === this.selectedAlgorithm && hasValidImage) {
              store.updateArtworkValues({
                index: 0,
                spangled: true,
                spangledImageUrl: effectiveImageUrl,
                ...(result["pages_info"] ? { spanglePagesInfo: result["pages_info"] } : {}),
                ...(result["positions_json"]
                  ? { spanglePositionsJson: result["positions_json"] }
                  : {})
              });
            }
          })
          .catch((error: unknown) => {
            notifyError("DTF spangle color change error", {
              params: {
                error_message: (error as Error).message,
                algorithm: variation.algorithm,
                pages_info: colorValues
              }
            });
          });
      });
      /* eslint-enable camelcase */
    },
    getVariation({
      algorithm,
      spanglePagesInfo,
      id,
      algorithmsSize,
      algorithmToAnimate,
      processTimeStamp
    }: {
      algorithm: string;
      spanglePagesInfo: Record<string, unknown> | null | undefined;
      id: string | number | null;
      algorithmsSize: number;
      algorithmToAnimate: string;
      processTimeStamp: string | number;
    }) {
      const { artworks, cable } = dtfStore;

      /* eslint-disable no-underscore-dangle */
      (
        cable as Record<string, unknown> & { spangle: (args: Record<string, unknown>) => void }
      ).spangle({
        id,
        version_identifier: algorithm,
        pages_info: spanglePagesInfo,
        algorithms_size: algorithmsSize,
        algorithm_to_animate: algorithmToAnimate,
        process_time_stamp: processTimeStamp,
        width: artworks[0].width,
        height: artworks[0].height
      });
      /* eslint-enable no-underscore-dangle */
    },
    /* eslint-enable max-lines-per-function */
    getSpangleVariations({
      isRetry,
      processTimeStamp
    }: {
      isRetry: boolean;
      processTimeStamp: string | number;
    }) {
      this.resetCompletedSpangleData();
      const { artworks, spangleAlgorithms } = dtfStore;

      if (!spangleAlgorithms.length) {
        return;
      }

      const { id } = artworks[0];

      this.prepareVariations({ isRetry });

      this.setSpangleMode(SPANGLE_MODES.spanglesLoading);
      this.setInvalidSpangleSelected(false);
      this.setIsSpangleReordering(false);

      const algorithmsToCall = this.spangleVariations.filter(
        ({ isLoading }: SpangleVariation) => isLoading
      );

      algorithmsToCall.map(({ algorithm, spanglePagesInfo }: SpangleVariation) =>
        this.getVariation({
          algorithm,
          spanglePagesInfo,
          id,
          algorithmsSize: algorithmsToCall.length,
          algorithmToAnimate: algorithmsToCall[0].algorithm,
          processTimeStamp
        })
      );
    },
    showSpangleResult() {
      const { artworks, updateArtworkValues } = dtfStore;

      this.setLastSpangledDimensions({
        width: artworks[0]?.width,
        height: artworks[0]?.height
      });

      this.completedSpangleData.forEach((data: SpangleData) => {
        this.updateSpangleVersion({ spangle: data });
      });

      // We want to select the first successful spangled algorithm
      const firstSuccessfuleSpangle = this.spangleVariations.find(
        (data: SpangleVariation) => !data.error
      );
      this.selectVersion({ version: firstSuccessfuleSpangle });

      updateArtworkValues({ index: 0, isLoading: false });
    },
    cancelSpangleProcess({ artwork }: { artwork: { id: string | number } }) {
      const { spangleVariations, lastSpangledDimensions } = this;
      const { updateArtworkValuesById } = dtfStore;
      const updatedList = spangleVariations.map((variation: Record<string, unknown>) => ({
        ...variation,
        isLoading: false
      }));

      this.setSpangleVariations(updatedList);
      updateArtworkValuesById({
        id: artwork.id,
        width: parseFloat(lastSpangledDimensions.width),
        height: parseFloat(lastSpangledDimensions.height)
      });

      this.setSpangleMode(SPANGLE_MODES.sizeEdited);
      this.resetCompletedSpangleData();
    },
    async cancelArtworkProcess({ artwork }: { artwork: { id: string | number } }) {
      try {
        await api.dtf.cancelArtworkProcess({ id: artwork.id });
      } catch (error) {
        notifyError("DTF cancel artwork process error", {
          params: { error_message: (error as Error).message, artwork_id: artwork.id }
        });
      } finally {
        this.setSpangleMode(SPANGLE_MODES.sizeEdited);
        this.resetCompletedSpangleData();
      }
    }
  }
});

export const spanglesStore = useSpanglesStore.store;
